import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { timeClockAPI } from '../utils/api';
import brandLogo from '../assets/logo-centre-jour.png';

const TabletTimeClockKioskPage = () => {
  const { t } = useTranslation();
  const [lang, setLang] = useState(() => (
    localStorage.getItem('tabletTimeclockLang')
    || (String(i18n.language || '').toLowerCase().startsWith('en') ? 'en' : 'fr')
  ));
  const [clockNow, setClockNow] = useState(new Date());
  const [loginUser, setLoginUser] = useState('');
  const [loginPass, setLoginPass] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [token, setToken] = useState('');
  const [center, setCenter] = useState(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [activeUser, setActiveUser] = useState(null);
  const [pin, setPin] = useState('');
  const [actionLoading, setActionLoading] = useState('');
  const [feedback, setFeedback] = useState({ type: 'idle', message: '' });
  const pinInputRef = useRef(null);

  const sortedResults = useMemo(() => {
    const normalizedQuery = query.trim().toLocaleLowerCase('fr-CA');
    const filtered = normalizedQuery
      ? results.filter((staff) => {
        const haystack = [
          staff.first_name,
          staff.last_name,
          `${staff.first_name || ''} ${staff.last_name || ''}`.trim(),
          staff.employee_number,
        ]
          .filter(Boolean)
          .join(' ')
          .toLocaleLowerCase('fr-CA');
        return haystack.includes(normalizedQuery);
      })
      : results;

    return [...filtered].sort((a, b) => {
      const aName = `${a.first_name || ''} ${a.last_name || ''}`.trim();
      const bName = `${b.first_name || ''} ${b.last_name || ''}`.trim();
      return aName.localeCompare(bName, 'fr-CA', { sensitivity: 'base' });
    });
  }, [query, results]);

  const refreshStaff = async (searchValue = query) => {
    if (!token) return;
    setSearchLoading(true);
    try {
      const response = await timeClockAPI.kioskSearchStaff({ q: searchValue }, token);
      const nextResults = response.data || [];
      setResults(nextResults);
      return nextResults;
    } catch (err) {
      setResults([]);
      return [];
    } finally {
      setSearchLoading(false);
    }
  };

  useEffect(() => {
    const timer = setInterval(() => setClockNow(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    localStorage.setItem('tabletTimeclockLang', lang);
    i18n.changeLanguage(lang);
  }, [lang]);

  useEffect(() => {
    if (!token) return;
    refreshStaff('');
  }, [token]);

  useEffect(() => {
    if (!token) return undefined;
    const timer = setTimeout(() => {
      refreshStaff(query);
    }, 180);
    return () => clearTimeout(timer);
  }, [query, token]);

  const nowDate = useMemo(
    () => new Intl.DateTimeFormat(lang === 'en' ? 'en-CA' : 'fr-CA', {
      timeZone: 'America/Toronto',
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(clockNow),
    [clockNow, lang]
  );

  const nowTime = useMemo(
    () => new Intl.DateTimeFormat(lang === 'en' ? 'en-CA' : 'fr-CA', {
      timeZone: 'America/Toronto',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    }).format(clockNow),
    [clockNow, lang]
  );

  const tx = (frText, enText) => (lang === 'en' ? enText : frText);
  const closeValidation = () => {
    setActiveUser(null);
    setPin('');
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoginLoading(true);
    setLoginError('');
    try {
      const response = await timeClockAPI.kioskLogin({ username: loginUser, password: loginPass });
      setToken(response.data?.accessToken || '');
      setCenter(response.data?.center || null);
      setFeedback({ type: 'idle', message: '' });
    } catch (err) {
      setLoginError(err.response?.data?.message || tx('Connexion kiosque invalide', 'Invalid kiosk login'));
    } finally {
      setLoginLoading(false);
    }
  };

  const handleClock = async (action) => {
    if (!activeUser || !pin || !token) return;
    setActionLoading(action);
    setFeedback({ type: 'idle', message: '' });
    try {
      const selectedUserId = activeUser.id;
      const response = await timeClockAPI.kioskClock({
        userId: selectedUserId,
        pin,
        action,
        deviceId: 'TABLET-KIOSK-01',
      }, token);
      setFeedback({
        type: 'success',
        message: tx(
          `${response.data?.user?.name || ''} pointe ${action === 'in' ? 'en entree' : 'en sortie'}.`,
          `${response.data?.user?.name || ''} clocked ${action === 'in' ? 'in' : 'out'}.`
        ),
      });
      await refreshStaff(query);
      closeValidation();
    } catch (err) {
      setFeedback({
        type: 'error',
        message: err.response?.data?.message || tx('Erreur de pointage', 'Clock action error'),
      });
    } finally {
      setActionLoading('');
    }
  };

  const isValidationScreen = Boolean(activeUser);
  const canClockIn = Boolean(activeUser?.has_timeclock_pin) && !activeUser?.is_clocked_in;
  const canClockOut = Boolean(activeUser?.has_timeclock_pin) && Boolean(activeUser?.is_clocked_in);

  useEffect(() => {
    if (!isValidationScreen) return;
    const timer = setTimeout(() => {
      pinInputRef.current?.focus();
      pinInputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 0);
    return () => clearTimeout(timer);
  }, [isValidationScreen]);

  if (!token) {
    return (
      <div style={{ minHeight: '100vh', display: 'grid', placeItems: 'center', background: '#0f172a', padding: 24 }}>
        <div style={{ position: 'fixed', top: 16, right: 16, display: 'flex', gap: 8 }}>
          <button type="button" className={`btn btn-sm ${lang === 'fr' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setLang('fr')}>FR</button>
          <button type="button" className={`btn btn-sm ${lang === 'en' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setLang('en')}>EN</button>
        </div>
        <form onSubmit={handleLogin} style={{ width: '100%', maxWidth: 460, background: '#fff', borderRadius: 18, padding: 28, boxShadow: '0 16px 42px rgba(0,0,0,.35)' }}>
          <div style={{ display: 'grid', placeItems: 'center', gap: 12, marginBottom: 18 }}>
            <img src={brandLogo} alt="Logo Centre de Jour" style={{ width: 180, objectFit: 'contain' }} />
            <h2 style={{ margin: 0 }}>{tx('Kiosque tablette du personnel', 'Staff tablet kiosk')}</h2>
          </div>
          {loginError ? <div className="alert alert-error" role="alert">⚠ {loginError}</div> : null}
          <div className="form-group">
            <label className="form-label">{tx('Utilisateur kiosque', 'Kiosk username')}</label>
            <input className="form-input" value={loginUser} onChange={(e) => setLoginUser(e.target.value)} required />
          </div>
          <div className="form-group">
            <label className="form-label">{tx('Mot de passe', 'Password')}</label>
            <input type="password" className="form-input" value={loginPass} onChange={(e) => setLoginPass(e.target.value)} required />
          </div>
          <button type="submit" className="btn btn-primary btn-lg" style={{ width: '100%', justifyContent: 'center' }} disabled={loginLoading}>
            {loginLoading ? t('common.loading') : tx('Ouvrir le kiosque', 'Open kiosk')}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg, #eff6ff 0%, #f8fafc 100%)', padding: 20 }}>
      <div className="tablet-kiosk-shell" style={{ maxWidth: 1100, margin: '0 auto', display: 'grid', gap: 20 }}>
        <div className="card tablet-kiosk-header" style={{ padding: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16 }}>
          <div>
            <div style={{ fontSize: 14, color: 'var(--gray-500)' }}>{nowDate}</div>
            <h1 style={{ margin: '6px 0 4px', fontSize: 34 }}>{nowTime}</h1>
            <div style={{ fontWeight: 700 }}>{center?.name}</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className={`btn btn-sm ${lang === 'fr' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setLang('fr')}>FR</button>
            <button type="button" className={`btn btn-sm ${lang === 'en' ? 'btn-primary' : 'btn-outline'}`} onClick={() => setLang('en')}>EN</button>
          </div>
        </div>

        {feedback.message ? (
          <div className={`alert ${feedback.type === 'success' ? 'alert-success' : 'alert-error'}`} role="alert">
            {feedback.type === 'success' ? '✓' : '⚠'} {feedback.message}
          </div>
        ) : null}

        <div className="card" style={{ padding: 20 }}>
          <div className="form-group">
            <label className="form-label">{tx('Rechercher un employe', 'Search staff')}</label>
            <input
              className="form-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={tx('Nom, prenom ou numero employe', 'Name, first name or employee number')}
            />
          </div>

          <div
            className="tablet-kiosk-main-grid"
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr)',
              gap: 20,
            }}
          >
            <div className="tablet-kiosk-list-panel">
              <div style={{ fontWeight: 700, marginBottom: 10 }}>
                {searchLoading ? tx('Recherche...', 'Searching...') : tx('Employes', 'Staff')}
              </div>
              <div className="tablet-kiosk-results" style={{ display: 'grid', gap: 10, maxHeight: 480, overflowY: 'auto' }}>
                {sortedResults.map((staff) => (
                  <div
                    key={staff.id}
                    className="tablet-kiosk-staff-row"
                    role="button"
                    tabIndex={0}
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      padding: '14px 16px',
                      width: '100%',
                      borderRadius: 14,
                      border: '2px solid',
                      background: activeUser?.id === staff.id ? 'var(--primary-50)' : 'white',
                      borderColor: activeUser?.id === staff.id ? 'var(--primary)' : 'var(--gray-300)',
                      color: 'var(--gray-800)',
                      boxShadow: activeUser?.id === staff.id ? '0 0 0 2px rgba(33, 104, 173, 0.12)' : 'none',
                      cursor: 'pointer',
                    }}
                    onClick={(event) => {
                      event.stopPropagation();
                      setActiveUser(staff);
                      setPin('');
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                        setActiveUser(staff);
                        setPin('');
                      }
                    }}
                  >
                    <span>{staff.first_name} {staff.last_name}</span>
                    <span style={{ fontSize: 12, opacity: 0.85 }}>
                      {staff.employee_number || '—'} {staff.is_clocked_in ? '• En entree' : '• Sorti'}
                    </span>
                  </div>
                ))}
                {!searchLoading && sortedResults.length === 0 ? (
                  <div className="text-muted">{tx('Aucun employe trouve', 'No staff found')}</div>
                ) : null}
              </div>
            </div>
          </div>
        </div>

        {isValidationScreen ? (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15, 23, 42, 0.55)',
              display: 'grid',
              placeItems: 'center',
              padding: 20,
              zIndex: 1000,
            }}
          >
            <div
              className="card"
              style={{
                width: '100%',
                maxWidth: 520,
                padding: 24,
                background: '#f8fafc',
                border: '1px solid var(--gray-200)',
              }}
              onClick={(event) => event.stopPropagation()}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
                <h3 style={{ marginTop: 0, marginBottom: 14 }}>{tx('Validation', 'Validation')}</h3>
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={closeValidation}
                >
                  {tx('Fermer', 'Close')}
                </button>
              </div>
              <div style={{ fontWeight: 700, fontSize: 20 }}>{activeUser.first_name} {activeUser.last_name}</div>
              <div className="text-muted" style={{ marginBottom: 14 }}>{activeUser.employee_number || '—'} {activeUser.job_function ? `• ${activeUser.job_function}` : ''}</div>
              <div className={`alert ${activeUser.is_clocked_in ? 'alert-success' : 'alert-warning'}`} role="status">
                {activeUser.is_clocked_in
                  ? tx('Employe actuellement en entree. Seule la sortie est disponible.', 'Staff member is currently clocked in. Only clock out is available.')
                  : tx('Employe actuellement sorti. Seule l entree est disponible.', 'Staff member is currently clocked out. Only clock in is available.')}
              </div>
              <div className="form-group">
                <label className="form-label">{tx('PIN personnel', 'Personal PIN')}</label>
                <input
                  ref={pinInputRef}
                  type="password"
                  inputMode="numeric"
                  autoComplete="new-password"
                  autoCorrect="off"
                  autoCapitalize="off"
                  spellCheck={false}
                  data-lpignore="true"
                  data-1p-ignore="true"
                  name={`timeclock-kiosk-pin-${activeUser?.id || 'none'}`}
                  className="form-input"
                  value={pin}
                  onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 12))}
                  placeholder="••••"
                />
              </div>
              {!activeUser.has_timeclock_pin ? (
                <div className="alert alert-warning" role="status">
                  {tx('Aucun PIN configure pour cet employe.', 'No PIN configured for this staff member.')}
                </div>
              ) : null}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <button
                  type="button"
                  className="btn btn-success btn-lg"
                  onClick={() => handleClock('in')}
                  disabled={!pin || actionLoading === 'in' || !canClockIn}
                >
                  {actionLoading === 'in' ? t('common.loading') : tx('Entree', 'Clock in')}
                </button>
                <button
                  type="button"
                  className="btn btn-danger btn-lg"
                  onClick={() => handleClock('out')}
                  disabled={!pin || actionLoading === 'out' || !canClockOut}
                >
                  {actionLoading === 'out' ? t('common.loading') : tx('Sortie', 'Clock out')}
                </button>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default TabletTimeClockKioskPage;
